import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SendPatientLinkModal from '../components/SendPatientLinkModal';
import { PDFSelectionModal } from '../components/PDFSelectionModal';
import { clearDashboardSessions, getActiveDashboardSession, getDashboardToken } from '../utils/session';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api'
interface MemberInfo {
  firstName: string;
  lastName: string;
  medicalId: string;
  phone: string;
  email?: string;
  dateOfBirth: string;
}

interface PDF {
  formName: string;
  fileName: string;
  blobUrl: string;
  pageCount: number;
}

interface Submission {
  id: number;
  submissionId: string;
  memberInfo: MemberInfo;
  memberName: string;
  medicalId: string;
  submittedBy: string;
  status: string;
  signatureComplete?: boolean;
  signatureStatus?: 'not_signed' | 'signed_complete';
  pdfCount: number;
  pdfs: PDF[];
  createdAt: string;
  updatedAt?: string;
  lastEditedByName?: string | null;
}

interface SubmissionsResponse {
  submissions: Submission[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface Draft {
  draftId: string;
  memberName: string;
  medicalId: string;
  phone: string;
  draftStep: number;
  updatedAt: string;
  createdAt: string;
  submittedBy?: string;
  lastEditedByName?: string | null;
}

interface DraftsResponse {
  success: boolean;
  drafts: Draft[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ActivityLog {
  id: number;
  timestamp: string;
  action: string;
  resourceType: string;
  resourceId: string;
  userId: string;
  userName: string;
  ipAddress: string;
  success: boolean;
  phiAccessed: boolean;
  details?: any;
}

interface ActivityResponse {
  success: boolean;
  logs: ActivityLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface BulkJobResult {
  id: number;
  jobId: string;
  actionType: 'delete' | 'sms' | 'email';
  totalSelected: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  status: string;
  startedAt: string;
  finishedAt: string;
  createdAt: string;
  results: Array<{
    submissionId: string;
    memberName?: string;
    recipient?: string;
    status: string;
    reason?: string;
  }>;
}

export const AdminSubmissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const staffIdFilter = searchParams.get('staffId');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [searchMedicalId, setSearchMedicalId] = useState('');
  const [expandedSubmission, setExpandedSubmission] = useState<string | null>(null);
  const [sendingFor, setSendingFor] = useState<string | null>(null);
  const [deletingFor, setDeletingFor] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'submissions' | 'drafts' | 'bulk-history' | 'activity'>('submissions');
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [draftsPagination, setDraftsPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [draftsPage, setDraftsPage] = useState(1);
  const [deletingDraft, setDeletingDraft] = useState<string | null>(null);
  const [sendLinkSubmission, setSendLinkSubmission] = useState<Submission | null>(null);
  
  // PDF Selection Modal state
  const [pdfSelectionModal, setPDFSelectionModal] = useState<{
    isOpen: boolean;
    submission: Submission | null;
  }>({ isOpen: false, submission: null });

  // Bulk action state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState('');
  const [bulkJobs, setBulkJobs] = useState<BulkJobResult[]>([]);
  const [regeneratingFor, setRegeneratingFor] = useState<string | null>(null);

  // Activity log state
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityPagination, setActivityPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [activityPage, setActivityPage] = useState(1);
  const [activityActionFilter, setActivityActionFilter] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFromDate, setActivityFromDate] = useState('');
  const [activityToDate, setActivityToDate] = useState('');
  const [activityJumpPage, setActivityJumpPage] = useState('');

  const dashboardSession = getActiveDashboardSession();
  const dashboardUser = dashboardSession?.user;
  const isAdmin = dashboardSession?.role === 'admin';
  const dashboardTitle = isAdmin ? 'Admin Dashboard' : 'Staff Dashboard';

  useEffect(() => {
    const token = getDashboardToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (activeTab === 'submissions') {
      fetchSubmissions();
    } else if (activeTab === 'drafts') {
      fetchDrafts();
    } else if (activeTab === 'bulk-history') {
      fetchBulkJobs();
    } else if (activeTab === 'activity') {
      fetchActivityLogs();
    }
  }, [page, draftsPage, activityPage, activeTab, navigate, staffIdFilter]);

  const fetchSubmissions = async () => {
    setLoading(true);
    setError('');

    try {
      const token = getDashboardToken();
      const params: any = { page, limit: 20 };
      
      if (searchMedicalId) {
        params.medicalId = searchMedicalId;
      }
      if (staffIdFilter) {
        params.staffId = staffIdFilter;
      }

      const response = await axios.get<SubmissionsResponse>(
        `${API_BASE_URL}/forms/submissions`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSubmissions(response.data.submissions);
      setPagination(response.data.pagination);
    } catch (err: any) {
      if (err.response?.status === 401) {
        clearDashboardSessions();
        navigate('/admin/login');
      } else {
        setError('Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const nextPath = dashboardSession?.role === 'staff' ? '/staff/login' : '/admin/login';
    clearDashboardSessions();
    navigate(nextPath);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchSubmissions();
  };

  const toggleExpanded = (submissionId: string) => {
    setExpandedSubmission(expandedSubmission === submissionId ? null : submissionId);
  };

  const sendAccessLink = async (submission: Submission, mode: 'signature' | 'full') => {
    try {
      setSendingFor(`${submission.submissionId}:${mode}`);
      const token = getDashboardToken();
      const response = await axios.post(
        `${API_BASE_URL}/forms/submissions/${submission.submissionId}/send-link`,
        { mode, email: submission.memberInfo.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.data?.success) {
        alert('Failed to send link');
        return;
      }
      alert(`${mode === 'signature' ? 'Signature' : 'Full edit'} link sent${response.data.emailSent ? ' by email' : ''}.`);
      fetchSubmissions();
    } catch (error) {
      alert('Failed to send link');
    } finally {
      setSendingFor(null);
    }
  };

  const handleEdit = (submission: Submission) => {
    // Open PDF selection modal instead of directly navigating
    setPDFSelectionModal({
      isOpen: true,
      submission: submission
    });
  };

  const handleClosePDFModal = () => {
    setPDFSelectionModal({ isOpen: false, submission: null });
  };

  const handlePDFSelectionComplete = (selectedFormNames: string[]) => {
    const submission = pdfSelectionModal.submission;
    if (!submission) return;

    // Close modal first
    handleClosePDFModal();

    // Navigate to edit form with selected PDFs
    const selectedPDFsParam = selectedFormNames.join(',');
    navigate(
      `/submit-forms?editSubmissionId=${encodeURIComponent(submission.submissionId)}&selectedPDFs=${encodeURIComponent(selectedPDFsParam)}`
    );
  };

  const handleDelete = async (submission: Submission) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the submission for "${submission.memberName}" (Medical ID: ${submission.medicalId})?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeletingFor(submission.submissionId);
      const token = getDashboardToken();
      const response = await axios.delete(
        `${API_BASE_URL}/forms/submissions/${submission.submissionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setExpandedSubmission(null);
        fetchSubmissions();
      } else {
        alert('Failed to delete submission');
      }
    } catch (error) {
      alert('Failed to delete submission');
    } finally {
      setDeletingFor(null);
    }
  };

  const handleRegenerate = async (submission: Submission) => {
    const confirmed = window.confirm(
      `Regenerate PDFs for "${submission.memberName}"?\n\nThis will overwrite the existing PDFs with freshly generated ones.`
    );
    if (!confirmed) return;
    try {
      setRegeneratingFor(submission.submissionId);
      const token = getDashboardToken();
      const response = await axios.post(
        `${API_BASE_URL}/forms/submissions/${submission.submissionId}/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        alert(response.data.message || 'PDFs regenerated successfully.');
        fetchSubmissions();
      } else {
        alert(response.data?.error || 'Failed to regenerate PDFs');
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to regenerate PDFs');
    } finally {
      setRegeneratingFor(null);
    }
  };

  const fetchDrafts = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getDashboardToken();
      const response = await axios.get<DraftsResponse>(
        `${API_BASE_URL}/forms/drafts`,
        { params: { page: draftsPage, limit: 20 }, headers: { Authorization: `Bearer ${token}` } }
      );
      setDrafts(response.data.drafts);
      setDraftsPagination(response.data.pagination);
    } catch (err: any) {
      setError('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  const fetchBulkJobs = async () => {
    try {
      const token = getDashboardToken();
      const response = await axios.get(`${API_BASE_URL}/forms/bulk-jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.success) {
        setBulkJobs(response.data.jobs);
      }
    } catch (err) {
      console.error('Failed to fetch bulk jobs:', err);
    }
  };

  const fetchActivityLogs = async (overridePage?: number) => {
    setActivityLoading(true);
    try {
      const token = getDashboardToken();
      const params: any = { page: overridePage ?? activityPage, limit: 50 };
      if (activityActionFilter) params.action = activityActionFilter;
      if (activityFromDate) params.from = activityFromDate;
      if (activityToDate) params.to = activityToDate + 'T23:59:59';
      const response = await axios.get<ActivityResponse>(`${API_BASE_URL}/forms/activity`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setActivityLogs(response.data.logs);
        setActivityPagination(response.data.pagination);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        clearDashboardSessions();
        navigate('/admin/login');
      }
    } finally {
      setActivityLoading(false);
    }
  };

  const handleResumeDraft = (draft: Draft) => {
    navigate(`/submit-forms?draftId=${encodeURIComponent(draft.draftId)}`);
  };

  const handleDeleteDraft = async (draft: Draft) => {
    const confirmed = window.confirm(`Are you sure you want to delete the draft for "${draft.memberName}"?`);
    if (!confirmed) return;
    try {
      setDeletingDraft(draft.draftId);
      const token = getDashboardToken();
      const response = await axios.delete(
        `${API_BASE_URL}/forms/drafts/${draft.draftId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        fetchDrafts();
      } else {
        alert('Failed to delete draft');
      }
    } catch (error) {
      alert('Failed to delete draft');
    } finally {
      setDeletingDraft(null);
    }
  };

  // Clear selection when page or tab changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, activeTab]);

  const toggleSelect = useCallback((submissionId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(submissionId)) next.delete(submissionId);
      else next.add(submissionId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === submissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(submissions.map(s => s.submissionId)));
    }
  }, [selectedIds.size, submissions]);

  const selectUnsigned = useCallback(() => {
    const unsigned = submissions.filter(s => s.signatureStatus !== 'signed_complete');
    setSelectedIds(new Set(unsigned.map(s => s.submissionId)));
  }, [submissions]);

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const confirmed = window.confirm(`Are you sure you want to delete ${ids.length} submission(s)?\n\nThis action cannot be undone.`);
    if (!confirmed) return;

    setBulkRunning(true);
    setBulkProgress(`Deleting ${ids.length} submissions...`);

    try {
      const token = getDashboardToken();
      const response = await axios.post(
        `${API_BASE_URL}/forms/submissions/bulk-delete`,
        { submissionIds: ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      setSelectedIds(new Set());
      fetchSubmissions();
      fetchBulkJobs();
      setBulkProgress(`Deleted ${data.deleted} of ${data.total}. ${data.failed > 0 ? `${data.failed} failed.` : ''}`);
      setTimeout(() => setBulkProgress(''), 5000);
    } catch (err) {
      setBulkProgress('Bulk delete failed');
      setTimeout(() => setBulkProgress(''), 5000);
    } finally {
      setBulkRunning(false);
    }
  };

  const handleBulkSend = async (channel: 'sms' | 'email') => {
    const ids = Array.from(selectedIds);
    const label = channel === 'sms' ? 'SMS' : 'Email';
    const confirmed = window.confirm(`Send ${label} to ${ids.length} selected submission(s)?\n\nRows without a valid ${channel === 'sms' ? 'phone number' : 'email'} will be skipped.`);
    if (!confirmed) return;

    setBulkRunning(true);
    setBulkProgress(`Sending ${label} to ${ids.length} recipients...`);

    try {
      const token = getDashboardToken();
      const response = await axios.post(
        `${API_BASE_URL}/forms/submissions/bulk-send`,
        { submissionIds: ids, channel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      setSelectedIds(new Set());
      fetchBulkJobs();
      setBulkProgress(`${label}: ${data.sent} sent, ${data.skipped} skipped, ${data.failed} failed`);
      setTimeout(() => setBulkProgress(''), 5000);
    } catch (err) {
      setBulkProgress(`Bulk ${label} failed`);
      setTimeout(() => setBulkProgress(''), 5000);
    } finally {
      setBulkRunning(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getViewUrl = (submissionId: string, fileName: string) =>
    `${API_BASE_URL}/forms/preview/${submissionId}/${encodeURIComponent(fileName)}`;

  const getPrintUrl = (submissionId: string, fileName: string) =>
    `${API_BASE_URL}/forms/print/${submissionId}/${encodeURIComponent(fileName)}`;

  const handlePrint = (submissionId: string, fileName: string) => {
    const printUrl = getPrintUrl(submissionId, fileName);
    window.open(printUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardTitle}</h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Welcome, {dashboardUser?.firstName || 'User'} {dashboardUser?.lastName || ''}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <button
                onClick={() => navigate('/admin/referrals')}
                className="flex-1 sm:flex-none text-blue-600 hover:text-blue-700 font-medium text-sm sm:text-base"
              >
                Referrals
              </button>
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin/users')}
                  className="flex-1 sm:flex-none text-blue-600 hover:text-blue-700 font-medium text-sm sm:text-base"
                >
                  Users
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="flex-1 sm:flex-none bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition text-sm sm:text-base font-medium shadow-sm flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                Fill New Form
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 sm:flex-none bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-red-700 transition text-sm sm:text-base"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        
        {staffIdFilter && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-blue-800 font-medium">Filtered by Staff Member</span>
            </div>
            <button
              onClick={() => {
                setSearchParams({});
                setPage(1);
              }}
              className="text-sm bg-white text-blue-600 px-3 py-1.5 rounded border border-blue-200 hover:bg-blue-100 transition font-medium shadow-sm"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <input
              type="text"
              value={searchMedicalId}
              onChange={(e) => setSearchMedicalId(e.target.value)}
              placeholder="Search by Medical ID..."
              className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
            />
            <div className="flex gap-2 sm:gap-4">
              <button
                type="submit"
                className="flex-1 sm:flex-none bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-blue-700 transition text-sm sm:text-base"
              >
                Search
              </button>
              {searchMedicalId && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchMedicalId('');
                    setPage(1);
                    fetchSubmissions();
                  }}
                  className="flex-1 sm:flex-none bg-gray-500 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-gray-600 transition text-sm sm:text-base"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => { setActiveTab('submissions'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition ${activeTab === 'submissions' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
          >
            Submissions
          </button>
          <button
            onClick={() => { setActiveTab('drafts'); setDraftsPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition ${activeTab === 'drafts' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
          >
            Drafts
          </button>
          <button
            onClick={() => setActiveTab('bulk-history')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition inline-flex items-center gap-1.5 ${activeTab === 'bulk-history' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Bulk History{bulkJobs.length > 0 ? ` (${bulkJobs.length})` : ''}
          </button>
          {/* Activity Monitor tab — hidden for now, will enable later */}
          {/* {isAdmin && (
            <button
              onClick={() => { setActivityPage(1); setActiveTab('activity'); }}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition inline-flex items-center gap-1.5 ${activeTab === 'activity' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              Activity Monitor
            </button>
          )} */}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}
----
        {/* Loading */}
        {activeTab === 'activity' ? (
          /* ===== ACTIVITY MONITOR TAB ===== */
          (() => {
            const ACTION_LABELS: Record<string, { label: string; description: string }> = {
              'FORM_SUBMIT':          { label: 'Form Submitted',        description: 'A new form was submitted' },
              'PHI_VIEW':             { label: 'Patient Record Viewed', description: 'Patient data was viewed' },
              'PHI_DOWNLOAD':         { label: 'Patient PDF Downloaded',description: 'Patient PDF was downloaded' },
              // 'PHI_CREATE':           { label: 'Patient Record Created',description: 'New patient record created' },
              'PHI_UPDATE':           { label: 'Patient Record Edited', description: 'Patient record was edited' },
              'PHI_RETRIEVAL':        { label: 'Patient Record Retrieved', description: 'Patient record was retrieved' },
              'PDF_GENERATE':         { label: 'PDF Generated',         description: 'PDF documents were generated' },
              'PDF_GENERATE_FAILED':  { label: 'PDF Generation Failed', description: 'PDF generation failed' },
              'LOGIN':                { label: 'User Logged In',        description: 'A user logged into the system' },
              'LOGOUT':               { label: 'User Logged Out',       description: 'A user logged out' },
              'SIGNUP':               { label: 'New Staff Signup',      description: 'A new staff account was requested' },
              'APPROVED':             { label: 'Staff Account Approved',description: 'A staff account was approved' },
              'DISAPPROVED':          { label: 'Staff Account Rejected',description: 'A staff account was rejected' },
              'SUBMISSION_DELETED':   { label: 'Submission Deleted',    description: 'A form submission was deleted' },
              'DRAFT_SAVED':          { label: 'Draft Saved',           description: 'A form draft was saved' },
              'DRAFT_DELETED':        { label: 'Draft Deleted',         description: 'A form draft was deleted' },
              'BULK_DELETE':          { label: 'Bulk Delete',           description: 'Multiple records were deleted' },
              'BULK_SMS':             { label: 'Bulk SMS Sent',         description: 'SMS sent to multiple patients' },
              'BULK_EMAIL':           { label: 'Bulk Email Sent',       description: 'Email sent to multiple patients' },
              'PASSWORD_RESET':       { label: 'Password Reset',        description: 'A password was reset by admin' },
            };

            const getActionInfo = (action: string) =>
              ACTION_LABELS[action] || { label: action.replace(/_/g, ' '), description: '' };

            const getActionColors = (action: string) => {
              if (action.includes('DELETE') || action.includes('DISAPPROVED') || action.includes('FAILED'))
                return 'bg-red-100 text-red-800';
              if (action.includes('LOGIN') || action.includes('SIGNUP') || action.includes('APPROVED'))
                return 'bg-green-100 text-green-800';
              if (action.includes('SUBMIT') || action.includes('CREATE'))
                return 'bg-blue-100 text-blue-800';
              if (action.includes('UPDATE') || action.includes('EDIT') || action.includes('RESET'))
                return 'bg-amber-100 text-amber-800';
              if (action.includes('VIEW') || action.includes('DOWNLOAD') || action.includes('RETRIEVAL'))
                return 'bg-purple-100 text-purple-800';
              if (action.includes('PDF') || action.includes('GENERATE'))
                return 'bg-indigo-100 text-indigo-800';
              if (action.includes('BULK') || action.includes('SMS') || action.includes('EMAIL'))
                return 'bg-teal-100 text-teal-800';
              return 'bg-gray-100 text-gray-800';
            };

            return (
              <div className="space-y-4">
                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
                  <div className="flex flex-wrap gap-3 items-end">
                    {/* Action filter */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500">Activity Type</label>
                      <select
                        value={activityActionFilter}
                        onChange={e => setActivityActionFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">All Activity</option>
                        <optgroup label="Forms">
                          <option value="FORM_SUBMIT">Form Submitted</option>
                          <option value="PDF_GENERATE">PDF Generated</option>
                          <option value="DRAFT_SAVED">Draft Saved</option>
                          <option value="DRAFT_DELETED">Draft Deleted</option>
                        </optgroup>
                        <optgroup label="Patient Records">
                          <option value="PHI_VIEW">Patient Record Viewed</option>
                          <option value="PHI_DOWNLOAD">Patient PDF Downloaded</option>
                          <option value="PHI_CREATE">Patient Record Created</option>
                          <option value="PHI_UPDATE">Patient Record Edited</option>
                        </optgroup>
                        <optgroup label="Deletions">
                          <option value="SUBMISSION_DELETED">Submission Deleted</option>
                          <option value="BULK_DELETE">Bulk Delete</option>
                        </optgroup>
                        <optgroup label="Users">
                          <option value="LOGIN">User Logged In</option>
                          <option value="LOGOUT">User Logged Out</option>
                          <option value="SIGNUP">New Staff Signup</option>
                          <option value="APPROVED">Staff Approved</option>
                          <option value="DISAPPROVED">Staff Rejected</option>
                          <option value="PASSWORD_RESET">Password Reset</option>
                        </optgroup>
                        <optgroup label="Bulk Actions">
                          <option value="BULK_SMS">Bulk SMS Sent</option>
                          <option value="BULK_EMAIL">Bulk Email Sent</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* From date */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500">From Date</label>
                      <input
                        type="date"
                        value={activityFromDate}
                        onChange={e => setActivityFromDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    {/* To date */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500">To Date</label>
                      <input
                        type="date"
                        value={activityToDate}
                        onChange={e => setActivityToDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    {/* Search button */}
                    <button
                      onClick={() => { setActivityPage(1); fetchActivityLogs(1); }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition"
                    >
                      Search
                    </button>

                    {/* Clear filters */}
                    {(activityActionFilter || activityFromDate || activityToDate) && (
                      <button
                        onClick={() => {
                          setActivityActionFilter('');
                          setActivityFromDate('');
                          setActivityToDate('');
                          setActivityPage(1);
                          setTimeout(() => fetchActivityLogs(1), 0);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition"
                      >
                        Clear
                      </button>
                    )}

                    <span className="text-sm text-gray-500 ml-auto self-end">
                      Total: <span className="font-semibold text-gray-800">{activityPagination.total}</span> events
                    </span>
                  </div>
                </div>

                {/* Logs Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {activityLoading ? (
                    <div className="flex justify-center items-center h-48">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                    </div>
                  ) : activityLogs.length === 0 ? (
                    <div className="px-6 py-16 text-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      <p className="text-gray-500 text-sm">No activity found.</p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table */}
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">What Happened</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Who Did It</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">What Was Affected</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {activityLogs.map((log) => {
                              const info = getActionInfo(log.action);
                              return (
                                <tr key={log.id} className={`hover:bg-gray-50 ${log.action.includes('DELETE') ? 'bg-red-50' : ''}`}>
                                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getActionColors(log.action)}`}>
                                      {info.label}
                                    </span>
                                    {info.description && (
                                      <div className="text-xs text-gray-400 mt-0.5">{info.description}</div>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="text-sm font-semibold text-gray-900">
                                      {log.userName === 'Anonymous User' ? 'Anonymous' : log.userName}
                                    </div>
                                    <div className="text-xs text-gray-400">{log.userId === 'anonymous' ? 'Not logged in' : `ID: ${log.userId}`}</div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="text-xs font-medium text-gray-700 capitalize">{log.resourceType.replace(/_/g, ' ')}</div>
                                    <div className="font-mono text-xs text-gray-400">{log.resourceId.substring(0, 16)}...</div>
                                    {log.details && log.details.pdfCount && (
                                      <div className="text-xs text-indigo-600 mt-0.5">{log.details.pdfCount} PDF(s)</div>
                                    )}
                                    {log.details && log.details.memberName && (
                                      <div className="text-xs text-blue-600 mt-0.5">Patient: {log.details.memberName}</div>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                    {log.ipAddress}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex flex-col gap-1">
                                      {log.success ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Success</span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Failed</span>
                                      )}
                                      {log.phiAccessed && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Patient Data</span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="lg:hidden divide-y divide-gray-200">
                        {activityLogs.map((log) => {
                          const info = getActionInfo(log.action);
                          return (
                            <div key={log.id} className={`p-4 space-y-2 ${log.action.includes('DELETE') ? 'bg-red-50' : ''}`}>
                              <div className="flex justify-between items-start">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getActionColors(log.action)}`}>
                                  {info.label}
                                </span>
                                {log.success ? (
                                  <span className="text-xs text-green-600 font-medium">Success</span>
                                ) : (
                                  <span className="text-xs text-red-600 font-medium">Failed</span>
                                )}
                              </div>
                              <div className="text-sm font-semibold text-gray-900">{log.userName === 'Anonymous User' ? 'Anonymous' : log.userName}</div>
                              <div className="text-xs text-gray-500 capitalize">{log.resourceType.replace(/_/g, ' ')}</div>
                              <div className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString()}</div>
                              <div className="text-xs text-gray-400">{log.ipAddress}</div>
                              {log.phiAccessed && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Patient Data Accessed</span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Pagination */}
                      {activityPagination.totalPages > 1 && (
                        <div className="bg-white px-4 py-3 border-t border-gray-200">
                          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                            <div className="text-xs text-gray-700">
                              Page <span className="font-medium">{activityPagination.page}</span> of{' '}
                              <span className="font-medium">{activityPagination.totalPages}</span>
                              <span className="ml-2 text-gray-400">({activityPagination.total} total)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => { setActivityPage(1); fetchActivityLogs(1); }}
                                disabled={activityPage === 1}
                                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                                title="First page"
                              >«</button>
                              <button
                                onClick={() => { const p = activityPage - 1; setActivityPage(p); fetchActivityLogs(p); }}
                                disabled={activityPage === 1}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                              >Previous</button>
                              <button
                                onClick={() => { const p = activityPage + 1; setActivityPage(p); fetchActivityLogs(p); }}
                                disabled={activityPage === activityPagination.totalPages}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                              >Next</button>
                              <button
                                onClick={() => { setActivityPage(activityPagination.totalPages); fetchActivityLogs(activityPagination.totalPages); }}
                                disabled={activityPage === activityPagination.totalPages}
                                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                                title="Last page"
                              >»</button>
                              {/* Jump to page */}
                              <div className="flex items-center gap-1 ml-2">
                                <span className="text-xs text-gray-500">Go to:</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={activityPagination.totalPages}
                                  value={activityJumpPage}
                                  onChange={e => setActivityJumpPage(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      const p = Math.min(Math.max(1, parseInt(activityJumpPage)), activityPagination.totalPages);
                                      if (!isNaN(p)) { setActivityPage(p); fetchActivityLogs(p); setActivityJumpPage(''); }
                                    }
                                  }}
                                  placeholder="pg"
                                  className="w-14 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <button
                                  onClick={() => {
                                    const p = Math.min(Math.max(1, parseInt(activityJumpPage)), activityPagination.totalPages);
                                    if (!isNaN(p)) { setActivityPage(p); fetchActivityLogs(p); setActivityJumpPage(''); }
                                  }}
                                  className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition"
                                >Go</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })()
        ) : loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'bulk-history' ? (
              /* ===== BULK HISTORY TAB ===== */
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {bulkJobs.length === 0 ? (
                  <div className="px-6 py-16 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <p className="text-gray-500 text-sm">No bulk actions performed yet.</p>
                    <p className="text-gray-400 text-xs mt-1">Select submissions and use Bulk SMS, Bulk Email, or Delete to get started.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {bulkJobs.map((job) => (
                      <div key={job.jobId} className="px-4 sm:px-6 py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.actionType === 'delete' ? 'bg-red-100 text-red-800' :
                              job.actionType === 'sms' ? 'bg-green-100 text-green-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {job.actionType === 'delete' ? 'Delete' : job.actionType === 'sms' ? 'SMS' : 'Email'}
                            </span>
                            <span className="text-xs text-gray-500">{formatDate(job.finishedAt)}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500 text-xs">Total</p>
                            <p className="font-semibold text-gray-900">{job.totalSelected}</p>
                          </div>
                          {job.actionType === 'delete' ? (
                            <>
                              <div>
                                <p className="text-gray-500 text-xs">Deleted</p>
                                <p className="font-semibold text-green-600">{job.successCount}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Failed</p>
                                <p className="font-semibold text-red-600">{job.failedCount}</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <p className="text-gray-500 text-xs">Sent</p>
                                <p className="font-semibold text-green-600">{job.successCount}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Skipped</p>
                                <p className="font-semibold text-yellow-600">{job.skippedCount}</p>
                              </div>
                              <div>
                                <p className="text-gray-500 text-xs">Failed</p>
                                <p className="font-semibold text-red-600">{job.failedCount}</p>
                              </div>
                            </>
                          )}
                        </div>
                        {job.results && job.results.length > 0 && (
                          <div className="mt-3 max-h-48 overflow-y-auto">
                            <table className="min-w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left pr-3 pb-1">Member</th>
                                  {job.actionType !== 'delete' && <th className="text-left pr-3 pb-1">Recipient</th>}
                                  <th className="text-left pb-1">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {job.results.map((r, i) => (
                                  <tr key={i} className="border-t border-gray-100">
                                    <td className="pr-3 py-1">{r.memberName || r.submissionId.substring(0, 8)}</td>
                                    {job.actionType !== 'delete' && <td className="pr-3 py-1">{r.recipient || '-'}</td>}
                                    <td className="py-1">
                                      {r.status === 'sent' || r.status === 'deleted' ? (
                                        <span className="text-green-600">{r.status === 'sent' ? 'Sent' : 'Deleted'}</span>
                                      ) : r.status === 'skipped' ? (
                                        <span className="text-yellow-600">Skipped: {r.reason}</span>
                                      ) : (
                                        <span className="text-red-600">Failed: {r.reason}</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : activeTab === 'drafts' ? (
              /* ===== DRAFTS TAB ===== */
              <>
                {/* Stats */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
                  <div className="text-center p-3 sm:p-0">
                    <p className="text-2xl sm:text-3xl font-bold text-orange-600">{draftsPagination.total}</p>
                    <p className="text-xs sm:text-sm text-gray-600">Total Drafts</p>
                  </div>
                </div>

                {/* Drafts List */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Desktop Table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medical ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Drafted By</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Step</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Saved</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {drafts.length === 0 ? (
                          <tr><td colSpan={6} className="px-6 py-8 text-center text-gray-500">No drafts found</td></tr>
                        ) : (
                          drafts.map((draft) => (
                            <tr key={draft.draftId} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{draft.memberName?.trim() ? draft.memberName : 'Anonymous'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{draft.medicalId}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                                {draft.submittedBy || 'Anonymous'}
                                {draft.lastEditedByName && draft.lastEditedByName !== draft.submittedBy && (
                                  <div className="text-xs text-amber-600 mt-1">Edited by: {draft.lastEditedByName}</div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{draft.phone}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Step {draft.draftStep + 1}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(draft.updatedAt)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  {/* Resume */}
                                  <button onClick={() => handleResumeDraft(draft)} title="Resume" className="p-2 rounded-lg text-green-600 hover:bg-green-50 transition">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </button>
                                  {/* Delete */}
                                  <button onClick={() => handleDeleteDraft(draft)} disabled={deletingDraft === draft.draftId} title="Delete" className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition disabled:opacity-50">
                                    {deletingDraft === draft.draftId ? (
                                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                    ) : (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden">
                    {drafts.length === 0 ? (
                      <div className="px-6 py-8 text-center text-gray-500">No drafts found</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {drafts.map((draft) => (
                          <div key={draft.draftId} className="p-4">
                            <div className="space-y-3">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900 text-sm">{draft.memberName}</p>
                                  <p className="text-xs text-gray-500 mt-1">Medical ID: {draft.medicalId}</p>
                                </div>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Step {draft.draftStep + 1}
                                </span>
                              </div>
                              <div className="flex flex-col gap-1 text-xs text-gray-500">
                                <span><span className="font-medium text-gray-700">Drafted by:</span> {draft.submittedBy || 'Anonymous'}</span>
                                <span><span className="font-medium text-gray-700">Last saved:</span> {formatDate(draft.updatedAt)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleResumeDraft(draft)} title="Resume" className="flex-1 flex items-center justify-center gap-2 p-2 rounded-lg text-green-600 bg-green-50 hover:bg-green-100 transition">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="text-sm font-medium">Resume</span>
                                </button>
                                <button onClick={() => handleDeleteDraft(draft)} disabled={deletingDraft === draft.draftId} title="Delete" className="p-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition disabled:opacity-50">
                                  {deletingDraft === draft.draftId ? (
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Drafts Pagination */}
                  {draftsPagination.totalPages > 1 && (
                    <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                        <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
                          Showing page <span className="font-medium">{draftsPagination.page}</span> of{' '}
                          <span className="font-medium">{draftsPagination.totalPages}</span>
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
                          <button onClick={() => setDraftsPage(draftsPage - 1)} disabled={draftsPage === 1} className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm">Previous</button>
                          <button onClick={() => setDraftsPage(draftsPage + 1)} disabled={draftsPage === draftsPagination.totalPages} className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm">Next</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
            /* ===== SUBMISSIONS TAB ===== */
            <>
            {/* Stats */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="text-center p-3 sm:p-0">
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">{pagination.total}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Total Submissions</p>
                </div>
                <div className="text-center p-3 sm:p-0">
                  <p className="text-2xl sm:text-3xl font-bold text-green-600">{pagination.totalPages}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Total Pages</p>
                </div>
                <div className="text-center p-3 sm:p-0">
                  <p className="text-2xl sm:text-3xl font-bold text-purple-600">{submissions.length}</p>
                  <p className="text-xs sm:text-sm text-gray-600">Current Page</p>
                </div>
              </div>
            </div>

            {/* Quick Select */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={selectUnsigned}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-300 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                Select Unsigned ({submissions.filter(s => s.signatureStatus !== 'signed_complete').length})
              </button>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 sm:p-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <span className="text-sm font-medium text-indigo-800">
                  {selectedIds.size} submission{selectedIds.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleBulkSend('sms')}
                    disabled={bulkRunning}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                    Bulk SMS
                  </button>
                  <button
                    onClick={() => handleBulkSend('email')}
                    disabled={bulkRunning}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    Bulk Email
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkRunning}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Delete
                  </button>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Bulk Progress */}
            {bulkProgress && (
              <div className={`rounded-lg p-3 mb-4 text-sm font-medium ${bulkRunning ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' : 'bg-green-50 border border-green-200 text-green-800'}`}>
                {bulkRunning && (
                  <svg className="animate-spin inline h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                )}
                {bulkProgress}
              </div>
            )}

            {/* Submissions List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={submissions.length > 0 && selectedIds.size === submissions.length}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submission ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Member Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Medical ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PDFs
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submitted By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date / Edited
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {submissions.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                          No submissions found
                        </td>
                      </tr>
                    ) : (
                      submissions.map((submission) => (
                        (() => {
                          return (
                        <React.Fragment key={submission.submissionId}>
                          <tr className={`hover:bg-gray-50 ${selectedIds.has(submission.submissionId) ? 'bg-indigo-50' : ''}`}>
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(submission.submissionId)}
                                onChange={() => toggleSelect(submission.submissionId)}
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {submission.submissionId.substring(0, 8)}...
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {submission.memberName?.trim() ? submission.memberName : 'Anonymous'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {submission.medicalId}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {submission.memberInfo.phone}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {submission.pdfCount} PDFs
                              </span>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${submission.signatureStatus === 'signed_complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                {submission.signatureStatus === 'signed_complete' ? 'Signed Complete' : 'Not Signed'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                              {submission.submittedBy || 'Anonymous'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex flex-col items-start gap-1">
                              <span>{formatDate(submission.createdAt)}</span>
                              {submission.updatedAt && Math.abs(new Date(submission.updatedAt).getTime() - new Date(submission.createdAt).getTime()) > 5000 && (
                                <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200" title={`Edited on ${formatDate(submission.updatedAt)}`}>
                                  Edited{submission.lastEditedByName ? ` by ${submission.lastEditedByName}` : `: ${formatDate(submission.updatedAt)}`}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2">
                                {/* View */}
                                <button
                                  onClick={() => toggleExpanded(submission.submissionId)}
                                  title="View"
                                  className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                {/* Send Link */}
                                <button
                                  onClick={() => setSendLinkSubmission(submission)}
                                  title="Send Link to Patient"
                                  className="p-2 rounded-lg text-green-600 hover:bg-green-50 transition"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                  </svg>
                                </button>
                                {/* Edit */}
                                <button
                                  onClick={() => handleEdit(submission)}
                                  title="Edit"
                                  className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 transition"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                {/* Delete */}
                                <button
                                  onClick={() => handleDelete(submission)}
                                  disabled={deletingFor === submission.submissionId}
                                  title="Delete"
                                  className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                                >
                                  {deletingFor === submission.submissionId ? (
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedSubmission === submission.submissionId && (
                            <tr>
                              <td colSpan={8} className="px-6 py-4 bg-gray-50">
                                <div className="space-y-4">
                                  {/* Member Details */}
                                  <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">
                                      Member Information
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">DOB:</span>{' '}
                                        {new Date(submission.memberInfo.dateOfBirth).toLocaleDateString()}
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Email:</span>{' '}
                                        {submission.memberInfo.email || 'N/A'}
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Signature:</span>{' '}
                                        {submission.signatureStatus === 'signed_complete' ? 'Signed Complete' : 'Not Signed'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* PDFs */}
                                  <div>
                                    <h4 className="font-semibold text-gray-900 mb-2">
                                      Generated PDFs
                                    </h4>
                                    <div className="space-y-2">
                                      {submission.pdfs.map((pdf, index) => {
                                        const viewUrl = getViewUrl(submission.submissionId, pdf.fileName);
                                        return (
                                          <div
                                            key={index}
                                            className="flex items-center justify-between bg-white p-3 rounded border border-gray-200"
                                          >
                                            <div>
                                              <p className="font-medium text-gray-900">
                                                {pdf.formName}
                                              </p>
                                              <p className="text-xs text-gray-500">
                                                {pdf.pageCount} pages
                                              </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <a
                                                href={viewUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="bg-gray-700 text-white px-3 py-2 rounded hover:bg-gray-800 transition text-sm"
                                              >
                                                View
                                              </a>
                                              {/*
                                              <button
                                                type="button"
                                                onClick={() => handlePrint(submission.submissionId, pdf.fileName)}
                                                className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 transition text-sm"
                                              >
                                                Print
                                              </button>
                                              */}
                                              <a
                                                href={pdf.blobUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 transition text-sm"
                                              >
                                                Download
                                              </a>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Regenerate PDFs */}
                                  <div className="pt-2 border-t border-gray-200">
                                    <button
                                      onClick={() => handleRegenerate(submission)}
                                      disabled={regeneratingFor === submission.submissionId}
                                      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                                    >
                                      {regeneratingFor === submission.submissionId ? (
                                        <>
                                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                          Regenerating...
                                        </>
                                      ) : (
                                        <>
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                          Regenerate PDFs
                                        </>
                                      )}
                                    </button>
                                    <p className="text-xs text-gray-400 mt-1">Rebuilds all PDFs from the saved form data. Use if PDFs are missing or corrupted.</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                          );
                        })()
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden">
                {submissions.length === 0 ? (
                  <div className="px-6 py-8 text-center text-gray-500">
                    No submissions found
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {submissions.map((submission) => {
                      return (
                      <div key={submission.submissionId} className={`p-4 ${selectedIds.has(submission.submissionId) ? 'bg-indigo-50' : ''}`}>
                        <div className="space-y-3">
                          <div className="flex justify-between items-start">
                            <div className="flex items-start gap-3 flex-1">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(submission.submissionId)}
                                onChange={() => toggleSelect(submission.submissionId)}
                                className="h-4 w-4 mt-1 text-indigo-600 border-gray-300 rounded cursor-pointer"
                              />
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900 text-sm">{submission.memberName}</p>
                                <p className="text-xs text-gray-500 font-mono mt-1">
                                  {submission.submissionId.substring(0, 12)}...
                                </p>
                              </div>
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {submission.pdfCount} PDFs
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Medical ID:</span>
                              <p className="text-gray-900 font-medium">{submission.medicalId}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Phone:</span>
                              <p className="text-gray-900 font-medium">{submission.memberInfo.phone}</p>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-500">
                            {formatDate(submission.createdAt)}
                          </div>

                          <div className="flex items-center gap-2">
                            {/* View */}
                            <button
                              onClick={() => toggleExpanded(submission.submissionId)}
                              title="View"
                              className="flex-1 flex items-center justify-center gap-2 p-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="text-sm font-medium">{expandedSubmission === submission.submissionId ? 'Hide' : 'View'}</span>
                            </button>
                            {/* Send Link */}
                            <button
                              onClick={() => setSendLinkSubmission(submission)}
                              title="Send Link to Patient"
                              className="p-2 rounded-lg text-green-600 bg-green-50 hover:bg-green-100 transition"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                            </button>
                            {/* Edit */}
                            <button
                              onClick={() => handleEdit(submission)}
                              title="Edit"
                              className="p-2 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 transition"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            {/* Delete */}
                            <button
                              onClick={() => handleDelete(submission)}
                              disabled={deletingFor === submission.submissionId}
                              title="Delete"
                              className="p-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition disabled:opacity-50"
                            >
                              {deletingFor === submission.submissionId ? (
                                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                          
                          {expandedSubmission === submission.submissionId && (
                            <div className="pt-3 space-y-3 border-t border-gray-200">
                              {/* Member Details */}
                              <div>
                                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                                  Member Information
                                </h4>
                                <div className="space-y-1 text-xs">
                                  <div>
                                    <span className="text-gray-600">DOB:</span>{' '}
                                    <span className="text-gray-900">
                                      {new Date(submission.memberInfo.dateOfBirth).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Email:</span>{' '}
                                    <span className="text-gray-900">
                                      {submission.memberInfo.email || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* PDFs */}
                              <div>
                                <h4 className="font-semibold text-gray-900 text-sm mb-2">
                                  Generated PDFs
                                </h4>
                                <div className="space-y-2">
                                  {submission.pdfs.map((pdf, index) => {
                                    const viewUrl = getViewUrl(submission.submissionId, pdf.fileName);
                                    return (
                                      <div
                                        key={index}
                                        className="bg-gray-50 p-3 rounded-lg"
                                      >
                                        <div className="flex justify-between items-start mb-2">
                                          <div className="flex-1">
                                            <p className="font-medium text-gray-900 text-sm">
                                              {pdf.formName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {pdf.pageCount} pages
                                            </p>
                                          </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <a
                                            href={viewUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block bg-gray-700 text-white px-2 py-2 rounded hover:bg-gray-800 transition text-xs text-center"
                                          >
                                            View
                                          </a>
                                          {/*
                                          <button
                                            type="button"
                                            onClick={() => handlePrint(submission.submissionId, pdf.fileName)}
                                            className="block bg-green-600 text-white px-2 py-2 rounded hover:bg-green-700 transition text-xs text-center"
                                          >
                                            Print
                                          </button>
                                          */}
                                          <a
                                            href={pdf.blobUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block bg-blue-600 text-white px-2 py-2 rounded hover:bg-blue-700 transition text-xs text-center"
                                          >
                                            Download
                                          </a>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Regenerate PDFs — mobile */}
                              <div className="pt-2 border-t border-gray-200">
                                <button
                                  onClick={() => handleRegenerate(submission)}
                                  disabled={regeneratingFor === submission.submissionId}
                                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                                >
                                  {regeneratingFor === submission.submissionId ? (
                                    <>
                                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                      Regenerating...
                                    </>
                                  ) : (
                                    <>
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                      Regenerate PDFs
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                    <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
                      Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page === pagination.totalPages}
                        className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            </>
            )}
          </>
        )}

      </div>

      {/* Send Patient Link Modal */}
      <SendPatientLinkModal
        isOpen={!!sendLinkSubmission}
        onClose={() => setSendLinkSubmission(null)}
        submission={sendLinkSubmission}
      />

      {/* PDF Selection Modal */}
      <PDFSelectionModal
        isOpen={pdfSelectionModal.isOpen}
        generatedPDFs={pdfSelectionModal.submission?.pdfs || []}
        onClose={handleClosePDFModal}
        onEditSelected={handlePDFSelectionComplete}
      />
    </div>
  );
};

